import { useIdeasNodeStore } from "../../stores/ideaNodesStore";

import { YouTubeLogo } from "../../pages/RecommendationFeed/components/YtSharp";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSessionEphemeralWsRequest } from "../../hooks/useSessionEphemeralWsRequest";
import {
  AppendTextToIdeaNodeResponse,
  WebsocketSummarySessionResponse,
} from "../SummarySession";
import { usethreadModeStore } from "../../stores/threadModeStore";
import { MarkdownWithHighlightPowers } from "./MarkdownWithHighlightPowers";
import { MemoizedMarkdownWithHighlightPowers } from "./MemoizedMarkdownWithHighlightPowers";
import { CollapsibleMarkdown } from "./CollapsibleMarkdown";
import {
  PlusCircleOutlined,
  SearchOutlined,
  ShareAltOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import { css } from "@emotion/react";
import { isWebExtension } from "@sharpana/shared/utils/web_environment";
import { Popover, Spin, message } from "antd";
import { SearchActionPopover } from "./AIActionsButtons";
import { RefreshSummaryModal, ReloadIcon } from "./RefreshSummaryModal";
import { GradientLoadingText } from "@sharpana/ui/GradientLoadingText";
import { ShareModal } from "../../components/ShareModal";
import {
  isMobileDevice,
  isWebShareSupported,
  shareViaWebShareApi,
} from "../../utils/browserDetection";
import { buildShareUrl } from "../../utils/shareUtils";
import { useSearchParams } from "react-router-dom";
import { MOBILE_NAV_HEIGHT } from "@sharpana/shared/layout";
import { SummaryNavigationArrows } from "./SummaryNavigationArrows";

interface AppendTextToThreadNodeResponse
  extends WebsocketSummarySessionResponse {
  node_id_path: string;
  text_to_append: string;
  finished_stream: boolean;
}

export const ThreadMode = (props: { contentToSummarize: string }) => {
  const [contentLoaded, setContentLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const setChatPaneOpen = useIdeasNodeStore((state) => state.setChatPaneOpen);
  const showDebuggingUI = useIdeasNodeStore((state) => state.showDebuggingUI);
  const sessionMetadata = useIdeasNodeStore((state) => state.sessionMetadata);
  const setSessionMetadata = useIdeasNodeStore(
    (state) => state.setSessionMetadata
  );

  const summarizationMode = useIdeasNodeStore(
    (state) => state.summarizationMode
  );
  const rootNode = usethreadModeStore((state) => state.rootNode);
  const childrenNodes = usethreadModeStore((state) => state.childrenNodes);

  const appendTextToIdeaNode = usethreadModeStore(
    (state) => state.appendTextToIdeaNode
  );
  const appendTextToChildrenNode = usethreadModeStore(
    (state) => state.appendTextToChildrenNode
  );
  const restoreState = usethreadModeStore((state) => state.restoreState);

  // Use the new ephemeral WebSocket hook
  const {
    request,
    lastMessage,
    startSessionRequest,
    state: requestState,
  } = useSessionEphemeralWsRequest<any, any>();

  const requestSentRef = useRef(false);
  const firstAppendTextToNodeSeenRef = useRef(false);
  const isRegeneratingRef = useRef(false); // Track if we're in the middle of regenerating to avoid triggering slug change refetch (differentiaties if a slug change is from regeneration, not from the user clicking navigation arrows - so we don't interfere with it and simply let it stream)
  const modeJustChangedRef = useRef(false); // Flag to skip slug processing after mode change (since this is already handled by another useEffect)
  
  // Refs to preserve state before regeneration attempt (for restoration if invitation modal is shown)
  const preservedRootNodeRef = useRef<any>(null);
  const preservedChildrenNodesRef = useRef<any>(null);
  const preservedContentLoadedRef = useRef<boolean>(false);

  // Collapse/expand is store-driven (global default + overrides)
  const globalDefaultCollapsed = usethreadModeStore(
    (state) => state.globalDefaultCollapsed
  );
  const collapseAllNodes = usethreadModeStore(
    (state) => state.collapseAllNodes
  );
  const expandAllNodes = usethreadModeStore((state) => state.expandAllNodes);
  const applyInitialCollapseIfNeeded = usethreadModeStore(
    (state) => state.applyInitialCollapseIfNeeded
  );

  useEffect(() => {
    // Set flag to prevent slug arrow-based navigation (from SummaryNavigationArrows) logic from interfering
    modeJustChangedRef.current = true;

    // Reset the request flag when summarization mode changes
    // This is necessary to prevent multiple repeated requests from being sent
    requestSentRef.current = false;
    // Also reset contentLoaded state when changing between summarization modes
    setContentLoaded(false);
    firstAppendTextToNodeSeenRef.current = false;
  }, [summarizationMode]);

  // Reset first-message tracking when a new session or URL is loaded
  useEffect(() => {
    firstAppendTextToNodeSeenRef.current = false;
  }, [sessionMetadata?.sessionId, sessionMetadata?.targetUrl]);

  const cleanThreadState = usethreadModeStore((state) => state.cleanState);

  // Watch for slug param changes (e.g., from navigation arrows from SummaryNavigationArrows) and refetch (without a full page reload)
  const slugParam = searchParams.get("s");
  const previousSlugRef = useRef<string | null>(null);

  useEffect(() => {
    // If mode just changed, skip this effect entirely
    // The mode change is already being handled by the mode change useEffect and parent component
    if (modeJustChangedRef.current) {
      modeJustChangedRef.current = false; // Reset flag
      previousSlugRef.current = slugParam;
      return;
    }

    // Check if this is a navigation change from SummaryNavigationArrows (not initial load, regeneration, nor content still loading)
    const isCompatibleNavigationChange =
      slugParam && // New slug exists
      previousSlugRef.current !== null && // Had a previous slug
      previousSlugRef.current !== "" && // Previous slug wasn't empty
      previousSlugRef.current !== slugParam && // Slug actually changed
      !isRegeneratingRef.current && // Not regenerating
      contentLoaded; // Previous content was fully loaded

    if (isCompatibleNavigationChange) {
      // Clean state and trigger new fetch (navigation via arrows)
      cleanThreadState();
      setContentLoaded(false);
      requestSentRef.current = false;
      firstAppendTextToNodeSeenRef.current = false;
      // Start collapsed when navigating between versions
      collapseAllNodes();
    }

    // Always update slug ref after processing
    previousSlugRef.current = slugParam;

    // Reset regenerating flag after slug change is processed
    isRegeneratingRef.current = false;
  }, [slugParam, contentLoaded]);

  useEffect(() => {
    if (rootNode === null || rootNode === undefined) {
      if (!requestSentRef.current) {
        try {
          // Ensure we have session metadata before sending
          if (sessionMetadata?.sessionId && sessionMetadata?.targetUrl) {
            requestSentRef.current = true;
            try {
              startSessionRequest({
                action: "thread_summary",
                targetContent: props.contentToSummarize,
                summarization_mode: summarizationMode,
              });
            } catch (error) {
              console.error("Failed to send WebSocket message:", error);
              requestSentRef.current = false;
            }
          }
        } catch (error) {
          console.error("Failed to initiate WebSocket message:", error);
          requestSentRef.current = false;
        }
      }
    }
  }, [summarizationMode, startSessionRequest]);

  ////////////////////////////////////////////////////////////////////////////////
  ////////     Handle receiving a new JSON message from websocket:       /////////
  ////////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (lastMessage) {
      const _lastMessage = lastMessage as WebsocketSummarySessionResponse;

      // Handle invitation to create account response (for anonymous users)
      if (_lastMessage.response_type === "invitation_to_create_account") {
        // Restore the preserved state so user can see the previous summary
        if (preservedRootNodeRef.current && preservedChildrenNodesRef.current) {
          restoreState({
            rootNode: preservedRootNodeRef.current,
            childrenNodes: preservedChildrenNodesRef.current,
          });
        }
        setContentLoaded(preservedContentLoadedRef.current);
        
        // Reset flags
        requestSentRef.current = false;
        isRegeneratingRef.current = false;
        
        // Clear preserved refs
        preservedRootNodeRef.current = null;
        preservedChildrenNodesRef.current = null;
        preservedContentLoadedRef.current = false;
        
        return;
      }

      if (_lastMessage.response_type === "append_text_to_node") {
        const lastMessage = _lastMessage as AppendTextToIdeaNodeResponse;

        // If this is the first append_text_to_node message for this session+mode,
        // decide auto-collapse based on "rehydrate" value (and ignore subsequent messages).
        const matchesMode =
          summarizationMode === lastMessage.summarization_mode;
        if (matchesMode && !firstAppendTextToNodeSeenRef.current) {
          firstAppendTextToNodeSeenRef.current = true;
          if (lastMessage.rehydrate === true) {
            applyInitialCollapseIfNeeded({
              rehydrated: true,
              hasSlug: searchParams.get("s") !== null,
              deepLinked: searchParams.get("n") !== null,
            });
          }
        }

        if (lastMessage.finished_stream) {
          setContentLoaded(true);
        }

        if (lastMessage.interaction_slug) {
          // Set the 's' param in the search url
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              params.set("s", lastMessage.interaction_slug); // add or replace
              return params;
            },
            { replace: true }
          ); // keep history clean

          // Add the interaction_slug to the ideaNodeStore.sessionMetadata
          setSessionMetadata({
            ...sessionMetadata,
            slugParam: lastMessage.interaction_slug,
          });
        }

        if (lastMessage.finished_stream && !lastMessage.rehydrate) {
          return;
        } else if (summarizationMode != lastMessage.summarization_mode) {
          return; // This message corresponds to a different mode .... TODO: We should either cancel that flow, or, more likely, save it to a different store
        } else {
          // Auto-collapse (for shared/rehydrated sessions) is handled in VirtualNodeSplitter based on the
          // `autoCollapseRequested` prop (only set for the first message when rehydrate is true,
          // and skipped if deep-link ?n= is present).

          appendTextToIdeaNode(lastMessage);
          return;
        }
      } else if (_lastMessage.response_type === "append_text_to_thread_node") {
        const lastMessage = _lastMessage as AppendTextToThreadNodeResponse;
        // console.log('Will append text to thread node:')
        // console.log(lastMessage)
        if (lastMessage.finished_stream && !lastMessage.rehydrate) {
          return;
        } else if (summarizationMode != lastMessage.summarization_mode) {
          return; // This message corresponds to a different mode .... TODO: We should either cancel that flow, or, more likely, save it to a different store
        } else {
          appendTextToChildrenNode(
            lastMessage.node_id_path,
            lastMessage.text_to_append
          );
        }
      }
    }
  }, [lastMessage]);

  //   console.log('Root node::')
  //   console.log(rootNode)
  const isOnWebExtension = isWebExtension();

  // State for the loading animation dots
  // Removed loadingDots state - using GradientLoadingText component instead

  // Effect to animate the loading dots
  // Removed loadingDots animation effect - using GradientLoadingText component instead

  // Handler for regenerating summary
  const handleRegenerateSummarySubmit = useCallback(
    (params: {
      customPrompt: string;
      selectedModel: string;
      detailLevel: string;
      familiarityLevel: string;
    }) => {
      // Mark that we're regenerating to prevent slug change useEffect from triggering
      // in other words, we are saying: "The following slug change is from regeneration, not from the user clicking navigation arrows - don't interfere with it."
      isRegeneratingRef.current = true;

      // Set requestSentRef BEFORE cleaning state to defensively prevent (defensive programming/future proofing) the useEffect above from triggering another summary request (startSessionRequest)
      requestSentRef.current = true;

      // Preserve current state before cleaning (in case we need to restore for anonymous users)
      if (rootNode) {
        preservedRootNodeRef.current = rootNode;
        preservedChildrenNodesRef.current = { ...childrenNodes };
        preservedContentLoadedRef.current = contentLoaded;
      }

      // Clean the existing thread state to allow new summary to populate
      cleanThreadState();
      setContentLoaded(false);

      // Send request to backend with skipReusingOldResponse flag
      startSessionRequest({
        action: "thread_summary",
        targetContent: props.contentToSummarize,
        summarization_mode: summarizationMode,
        skipReusingOldResponse: true,
        customPrompt: params.customPrompt || undefined,
        selectedModel: params.selectedModel || undefined,
        detailLevel: params.detailLevel || undefined,
        familiarityLevel: params.familiarityLevel || undefined,
      });

      // message.success("Regenerating summary...");
    },
    [
      cleanThreadState,
      startSessionRequest,
      props.contentToSummarize,
      summarizationMode,
      rootNode,
      childrenNodes,
      contentLoaded,
    ]
  );

  // Regenerate Summary modal state
  const [regenerateSummaryModalVisible, setRegenerateSummaryModalVisible] =
    useState(false);

  ////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////

  return (
    <>
      {/* Persistent toolbar with regenerate button and navigation arrows */}
      <div css={toolbarContainerStyle}>
        <div css={leftControlsGroup}>
          <div
            css={controlButtonStyleDiscrete}
            onClick={() => setRegenerateSummaryModalVisible(true)}
          >
            <ReloadIcon />
          </div>
          <SummaryNavigationArrows
            resourceId={sessionMetadata?.resourceId}
            summarizationMode={summarizationMode}
            currentSlug={sessionMetadata?.slugParam}
            contentLoaded={contentLoaded}
          />
        </div>
        {rootNode && (
          <div
            css={controlButtonStyle}
            onClick={() => {
              if (globalDefaultCollapsed) {
                expandAllNodes();
              } else {
                collapseAllNodes();
              }
            }}
          >
            {globalDefaultCollapsed ? (
              <>
                <FullscreenOutlined css={iconStyle} />{" "}
                <span css={controlButtonLabel}>Expand All</span>
              </>
            ) : (
              <>
                <FullscreenExitOutlined css={iconStyle} />{" "}
                <span css={controlButtonLabel}>Collapse All</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Refresh Summary Modal */}
      <RefreshSummaryModal
        visible={regenerateSummaryModalVisible}
        onClose={() => setRegenerateSummaryModalVisible(false)}
        onSubmit={handleRegenerateSummarySubmit}
      />

      {rootNode ? (
        <div
          key={`${rootNode.id}-threadMode`}
          onClick={(e) => {
            setChatPaneOpen(false);
          }}
          css={isOnWebExtension ? null : centralContainerStyle}
        >
          {showDebuggingUI ? <div>Node id: {rootNode.id}</div> : <></>}
          <div>
            <VirtualNodeSplitter
              key={rootNode.id}
              rawText={rootNode.description}
            />
            {!contentLoaded && (
              <div
                css={css`
                  text-align: center;
                  margin: 20px 0;
                  padding: 15px;
                `}
              >
                <Spin size="default" />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          css={css`
            text-align: center;
            margin-top: 25px;
          `}
        >
          <Spin size="default" />
          <GradientLoadingText
            text="Generating summary"
            fontSize="16px"
            padding="1rem"
          />
        </div>
      )}

      {/* YouTube video button at the bottom - only shown when content is loaded */}
      {sessionMetadata?.ytVideoId && contentLoaded && (
        <div
          css={
            isOnWebExtension
              ? css`
                  text-align: center;
                  margin: 0 auto;
                  border-top-left-radius: 0;
                  border-top-right-radius: 0;
                  border-top: none;
                  padding: 5px 0;
                  min-height: 60px;
                  display: block;
                `
              : css`
                  ${centralContainerStyle}
                  text-align: center;
                  margin: 0 auto;
                  border-top-left-radius: 0;
                  border-top-right-radius: 0;
                  border-top: none;
                  padding: 5px 0;
                  min-height: 60px;
                  display: block;
                  display: flex;
                  justify-content: center;
                  @media (max-width: 768px) {
                    /* Leave space for the fixed mobile bottom nav and the chat textarea bar */
                    margin-bottom: calc(${MOBILE_NAV_HEIGHT} + 8px);
                  }
                `
          }
        >
          <a
            href={`https://www.youtube.com/watch?v=${sessionMetadata.ytVideoId}`}
            target="_blank"
            rel="noopener noreferrer"
            css={css`
              display: inline-block;
              padding: 8px 16px;
              background-color: #282828;
              border-radius: 4px;
              text-decoration: none;
              color: white;
              &:hover {
                background-color: #383838;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <YouTubeLogo />
              <span>Watch on YouTube</span>
            </div>
          </a>
        </div>
      )}
    </>
  );
};

const nodeSeparationColor = "rgba(220, 220, 220, 0.06)";
const nodeContentPaddingLeft = 34; // Padding for node content

const centralContainerStyle = css`
  border: 1px solid ${nodeSeparationColor};
  border-radius: 7px;
  padding: 7px;
  @media (max-width: 769px) {
    border: none;
  }

  /* Target only headings in expanded nodes for left padding */
  .expanded-node-content p,
  .expanded-node-content h1,
  .expanded-node-content h2,
  .expanded-node-content h3,
  .expanded-node-content h4,
  .expanded-node-content h5,
  .expanded-node-content h6,
  .expanded-node-content ul,
  .expanded-node-content ol {
    padding-left: ${nodeContentPaddingLeft}px;
  }

  /* Make sure embedded components stay full width by using negative margin */
  .expanded-node-content .ytFrameTopContainer {
    margin-left: -${nodeContentPaddingLeft}px;
    width: calc(
      100% + ${nodeContentPaddingLeft}px
    ); /* Compensate for the negative margin */
  }

  .expanded-node-content [class*="timelinesectionbar"] {
    margin-left: -${nodeContentPaddingLeft}px;
    width: calc(100% + ${nodeContentPaddingLeft}px);
  }

  .collapsed-node-content {
    height: 100%;
  }
`;

const toggleStyle = css`
  user-select: none;
  :hover {
    cursor: pointer;
  }
`;

const nodeSeparatorHr = css`
  height: 2px;
  border: none;
  background-color: ${nodeSeparationColor};
  opacity: 0.8;
`;

const nodeContainerStyle = css`
  position: relative;
  margin-bottom: 10px;
`;

const collapsedNodeStyle = css`
  width: 48%;
  display: inline-block;
  vertical-align: top;
  margin-right: 1%;
  margin-left: 1%;
  border: 1px solid rgba(75, 75, 75, 0.5);
  border-radius: 8px;
  // padding: 8px;
  transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease,
    background-color 0.3s ease;
  will-change: transform, box-shadow;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-color: rgba(75, 75, 75, 0.8);
    background-color: rgba(205, 205, 205, 0.05);
  }
`;

const expandedNodeStyle = css`
  width: 100%;
  transition: box-shadow 0.3s ease, opacity 0.3s ease;
  will-change: opacity, box-shadow;
`;

const highlightedNodeStyle = css`
  border: 3px solid rgba(214, 186, 99, 0.4);
  border-top: none;
  border-bottom: none;
  border-radius: 8px;
  box-shadow: 0 0 4px rgba(255, 215, 0, 0.2);
  width: 100%;
  transition: box-shadow 0.3s ease, border-color 0.3s ease, opacity 0.3s ease;
  will-change: box-shadow, opacity;
`;

const collapseToggleButton = css`
  position: absolute;
  top: 0px;
  left: 0px;
  background: rgba(70, 70, 70, 0.4);
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  opacity: 0.8;
  transition: background-color 0.3s ease, opacity 0.3s ease, transform 0.2s ease;
  will-change: opacity, transform;

  &:hover {
    opacity: 1;
    background: rgba(90, 90, 90, 0.5);
  }

  .arrow {
    width: 0;
    height: 0;
    display: inline-block;
    border-style: solid;
    transition: transform 0.3s ease;
  }

  .arrow-down {
    border-width: 8px 5px 0 5px;
    border-color: rgba(255, 255, 255, 0.9) transparent transparent transparent;
    transform: rotate(0deg);
  }

  .arrow-right {
    border-width: 5px 0 5px 8px;
    border-color: transparent transparent transparent rgba(255, 255, 255, 0.9);
    transform: rotate(0deg);
  }
`;

// Component to render a single virtual node
const VirtualNode = React.memo(
  (props: {
    virtualNode: IdeaNodeData;
    index: number;
    totalNodes: number;
    isLastNode: boolean;
    isHighlighted?: boolean;
    isVisited?: boolean;
    onExpand?: (index: number) => void;
  }) => {
    const { virtualNode, index, totalNodes, isLastNode } = props;
    const childrenNodes = usethreadModeStore((state) => state.childrenNodes);
    const nodeTopic = virtualNode.topic || "";
    const topicColor = nodeTopic ? getTopicColor(nodeTopic) : "";
    const isNodeCollapsed = usethreadModeStore((state) =>
      state.isNodeCollapsed(String(index))
    );
    const toggleNodeCollapse = usethreadModeStore(
      (state) => state.toggleNodeCollapse
    );

    const nodeIndexStr = String(index);
    const nodeRef = useRef<HTMLDivElement>(null);

    const handleCollapsedNodeClick = useCallback(() => {
      if (isNodeCollapsed) {
        // Mark as visited when expanding a node (only if parent activated tracking)
        props.onExpand?.(index);
        toggleNodeCollapse(nodeIndexStr);
        // Add a small delay to ensure DOM updates before scrolling
        setTimeout(() => {
          if (nodeRef.current) {
            const nodeRect = nodeRef.current.getBoundingClientRect();
            const pixelsOutOfViewportToTriggerScroll = 30;
            const isAboveViewport =
              nodeRect.top < pixelsOutOfViewportToTriggerScroll;
            const isBelowViewport =
              nodeRect.bottom >
              window.innerHeight - pixelsOutOfViewportToTriggerScroll;

            if (isAboveViewport || isBelowViewport) {
              const scrollPosition = window.scrollY + nodeRect.top - 40; // 40px above the node
              window.scrollTo({ top: scrollPosition, behavior: "smooth" });
            }
          }
        }, 50);
      }
    }, [isNodeCollapsed, toggleNodeCollapse, nodeIndexStr]);

    return (
      <div
        id={`node-${index}`}
        ref={nodeRef}
        css={[
          nodeContainerStyle,
          isNodeCollapsed ? collapsedNodeStyle : expandedNodeStyle,
          !isNodeCollapsed && props.isHighlighted ? highlightedNodeStyle : null,
          isNodeCollapsed && nodeTopic
            ? css`
                // border: 1px solid ${topicColor};
                border: 1px solid ${topicColor}4D; /* 4D = ~30% opacity in hex */
                // box-shadow: 0 2px 4px ${topicColor}20, 0 0px 8px ${topicColor}25;
                box-shadow: 0 12px 4px -3px #1d1d1d; /* contact */

                &:hover {
                  box-shadow: 0 4px 8px ${topicColor}25; /* 25 = 15% opacity in hex */
                }
              `
            : null,
        ]}
        onClick={handleCollapsedNodeClick}
      >
        {/* Toggle button only visible when expanded */}
        {!isNodeCollapsed && (
          <div
            css={collapseToggleButton}
            onClick={(e) => {
              e.stopPropagation();
              // Ensure node is marked visited when user collapses from expanded state
              props.onExpand?.(index);
              toggleNodeCollapse(nodeIndexStr);
            }}
          >
            <span className="arrow arrow-down"></span>
          </div>
        )}

        <div
          className={
            !isNodeCollapsed
              ? "expanded-node-content"
              : "collapsed-node-content"
          }
        >
          <CollapsibleMarkdown
            rawMarkdownContent={virtualNode.content}
            isCollapsed={isNodeCollapsed}
            visited={props.isVisited}
            nodeIndex={index}
            totalNodes={totalNodes}
            topic={nodeTopic}
            topicColor={topicColor}
          />
        </div>

        {!isNodeCollapsed && (
          <>
            <NodeActions
              nodeText={virtualNode.content}
              nodeIndex={nodeIndexStr}
              totalNodeCount={String(totalNodes)}
            />

            {nodeIndexStr in childrenNodes &&
              childrenNodes[nodeIndexStr].expanded && (
                <MemoizedMarkdownWithHighlightPowers
                  rawMarkdownContent={childrenNodes[index].text}
                />
              )}
          </>
        )}

        {/* Only show the horizontal separator if this is not the last node and not collapsed */}
        {!isLastNode && !isNodeCollapsed && <hr css={nodeSeparatorHr} />}
      </div>
    );
  }
);

const nodesContainerStyle = css`
  display: flex;
  flex-wrap: wrap;
`;

// Subtle gradient accent reused for borders; diagonal blue → purple range (further reduced opacity)
const accentGradient =
  "linear-gradient(135deg, rgba(154, 83, 229, 0.55), rgba(106, 120, 255, 0.55), rgba(74, 143, 227, 0.55))";

const toolbarContainerStyle = css`
  display: flex;
  justify-content: space-between;
  padding: 8px;
  margin-bottom: 0px;
  border: 1px solid rgba(75, 75, 75, 0.2);
  border-bottom: none;
  position: sticky;
  top: 0;
  // background: rgba(28, 28, 28, 0.95);
  backdrop-filter: blur(10px);
  z-index: 10;

  @media (max-width: 769px) {
    padding: 4px;
    border-bottom: 1px solid rgba(75, 75, 75, 0.2);
    border-top: none;
  }
`;

const controlsContainerStyle = css`
  display: flex;
  justify-content: flex-end;
  padding: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(75, 75, 75, 0.2);
`;

const leftControlsGroup = css`
  display: flex;
  align-items: center;
  gap: 0;
`;

const controlButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  cursor: pointer;
  margin-left: 8px;
  user-select: none;
  color: #f2f2f2;
  font-size: 14px;
  line-height: 1;
  min-height: 34px;
  background: linear-gradient(#2a2a2a, #2a2a2a) padding-box,
    ${accentGradient} border-box; /* neutral dark with subtle gradient border */
  border: 1px solid transparent;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.2s ease, transform 0.1s ease;

  &:hover {
    background: linear-gradient(#333333, #333333) padding-box,
      ${accentGradient} border-box;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    background: linear-gradient(#292929, #292929) padding-box,
      ${accentGradient} border-box;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.12),
      0 4px 12px rgba(0, 0, 0, 0.25);
  }
`;

const controlButtonStyleDiscrete = css`
  ${controlButtonStyle}
  border: 1px solid #3b3b3b;
  padding: 6px 8px;
`;

const iconStyle = css`
  margin-right: 6px;
  font-size: 1.1rem;
  transition: color 0.2s ease;
`;

const iconStyleNoRightMargin = css`
  font-size: 1.1rem;
  font-weight: 600;
  transition: color 0.2s ease;
`;

const controlButtonLabel = css`
  font-weight: 500;
  letter-spacing: 0.2px;
  color: inherit;
`;


// Define interface for node data with topic
interface IdeaNodeData {
  content: string;
  topic?: string;
}

// Store allocated colors to prevent duplicates or similar colors
const allocatedTopicColors: { [topic: string]: string } = {};

// Pre-defined color palette with visually distinct, pleasant colors
const distinctColorPalette = [
  "#4285f4", // Blue
  "#ea4335", // Red
  "#34a853", // Green
  "#fbbc05", // Yellow
  "#9c27b0", // Purple
  "#00acc1", // Teal
  "#ff7043", // Deep Orange
  "#3949ab", // Indigo
  "#8bc34a", // Light Green
  "#795548", // Brown
  "#607d8b", // Blue Grey
  "#e91e63", // Pink
  "#ff5722", // Orange
  "#009688", // Teal
  "#673ab7", // Deep Purple
  "#2196f3", // Light Blue
  "#ffeb3b", // Yellow
  "#ff9800", // Orange
  "#cddc39", // Lime
];

// Function to calculate color distance (simple Euclidean in RGB space)
const getColorDistance = (color1: string, color2: string): number => {
  // Convert hex to RGB
  const r1 = parseInt(color1.substring(1, 3), 16);
  const g1 = parseInt(color1.substring(3, 5), 16);
  const b1 = parseInt(color1.substring(5, 7), 16);

  const r2 = parseInt(color2.substring(1, 3), 16);
  const g2 = parseInt(color2.substring(3, 5), 16);
  const b2 = parseInt(color2.substring(5, 7), 16);

  // Calculate Euclidean distance
  return Math.sqrt(
    Math.pow(r1 - r2, 2) + Math.pow(g1 - g2, 2) + Math.pow(b1 - b2, 2)
  );
};

// Function to find the most distinct color from already allocated colors
const findDistinctColor = (): string => {
  const allocatedColors = Object.values(allocatedTopicColors);
  if (allocatedColors.length === 0) {
    return distinctColorPalette[0]; // Return first color if none allocated yet
  }

  if (allocatedColors.length >= distinctColorPalette.length) {
    // If we've used all colors, pick one based on minimum similarity
    let bestColor = distinctColorPalette[0];
    let maxMinDistance = -1;

    // For each candidate color, find its minimum distance to any allocated color
    for (const candidateColor of distinctColorPalette) {
      let minDistance = Number.MAX_VALUE;

      // Find minimum distance to any allocated color
      for (const allocatedColor of allocatedColors) {
        const distance = getColorDistance(candidateColor, allocatedColor);
        if (distance < minDistance) {
          minDistance = distance;
        }
      }

      // If this candidate has a better minimum distance, select it
      if (minDistance > maxMinDistance) {
        maxMinDistance = minDistance;
        bestColor = candidateColor;
      }
    }

    return bestColor;
  } else {
    // Pick the next unused color from the palette
    const usedIndices = new Set();
    for (const color of allocatedColors) {
      const index = distinctColorPalette.indexOf(color);
      if (index !== -1) {
        usedIndices.add(index);
      }
    }

    // Find first unused color
    for (let i = 0; i < distinctColorPalette.length; i++) {
      if (!usedIndices.has(i)) {
        return distinctColorPalette[i];
      }
    }

    // Fallback (shouldn't reach here based on our logic)
    return distinctColorPalette[0];
  }
};

// Function to generate a consistent color from a topic string
const getTopicColor = (topic: string): string => {
  // Return color if already allocated for this topic
  if (topic in allocatedTopicColors) {
    return allocatedTopicColors[topic];
  }

  // Get a new distinct color
  const color = findDistinctColor();
  allocatedTopicColors[topic] = color;

  return color;
};

const VirtualNodeSplitter = (props: {
  rawText: string;
}) => {
  const [searchParams] = useSearchParams();
  const text = props.rawText;

  // Access summarizationMode from store
  const summarizationMode = useIdeasNodeStore(
    (state) => state.summarizationMode
  );

  // Updated sentinelRegex to match new format with topic
  const sentinelRegex = /\|\|\|(HEADLINE|NODE(?::topic=([^\|]+))?)\|\|\|/g;

  // We'll use a different approach to split and capture groups
  let headlineBlock = "";
  const ideaNodes: IdeaNodeData[] = [];

  // Use regex to find all matches
  let match;
  let lastIndex = 0;

  while ((match = sentinelRegex.exec(text)) !== null) {
    const fullMatch = match[0];
    const type = match[1].startsWith("NODE") ? "NODE" : "HEADLINE";
    const topic = type === "NODE" ? match[2] || "" : "";

    // Find the end of this section (next sentinel or end of text)
    const nextMatchIndex = text.indexOf("|||", sentinelRegex.lastIndex);
    const endIndex = nextMatchIndex !== -1 ? nextMatchIndex : text.length;

    // Extract content
    const content = text.substring(sentinelRegex.lastIndex, endIndex).trim();

    if (content) {
      if (type === "HEADLINE") {
        headlineBlock = content;
      } else if (type === "NODE") {
        ideaNodes.push({
          content,
          topic,
        });
      }
    }

    lastIndex = sentinelRegex.lastIndex;
  }

  // Track which node indices have been expanded at least once after the initial collapse
  const [visitedNodes, setVisitedNodes] = useState<Set<number>>(new Set());
  const collapseAllNodes = usethreadModeStore((state) => state.collapseAllNodes);
  const expandNode = usethreadModeStore((state) => state.expandNode);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const toggleNodeCollapse = usethreadModeStore(
    (state) => state.toggleNodeCollapse
  );

  // Handle deep-linking to a specific node via ?n=<index>
  const deepLinkedNodeRef = useRef<boolean>(false);
  useEffect(() => {
    if (deepLinkedNodeRef.current) return; // run only once
    const nParam = searchParams.get("n");
    if (nParam !== null) {
      const targetIndex = parseInt(nParam, 10);
      if (
        !isNaN(targetIndex) &&
        targetIndex >= 0 &&
        targetIndex < ideaNodes.length
      ) {
        // Collapse all, then expand target
        collapseAllNodes();
        expandNode(targetIndex);
        setHighlightedIndex(targetIndex);
        // Mark deep-linked target as visited since it's expanded after initial collapse
        setVisitedNodes((prev) => {
          const next = new Set(prev);
          next.add(targetIndex);
          return next;
        });
        // Scroll into view after DOM paint. We perform an initial scroll and
        // then a follow-up adjustment once the node has had time to fully
        // expand/stream its contents. This prevents the viewport from ending
        // up **below** the node when its height changes after the first scroll.
        const scrollToTarget = (smooth: boolean) => {
          const el = document.getElementById(`node-${targetIndex}`);
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const scrollPosition = window.scrollY + rect.top - 40; // 30px padding from the top
          window.scrollTo({
            top: scrollPosition,
            behavior: smooth ? "smooth" : "auto",
          });
        };

        // First attempt shortly after the DOM updates (collapse/expand)
        setTimeout(() => {
          scrollToTarget(true);
          // Second attempt after additional layout shifts (e.g. markdown streaming)
          setTimeout(() => scrollToTarget(false), 400);
        }, 100);
      }
    }
    deepLinkedNodeRef.current = true;
  }, [ideaNodes.length, searchParams, collapseAllNodes, expandNode]);

  // UseRef to track previous length of idea nodes (kept minimal)
  const prevNodesLengthRef = useRef(0);
  useEffect(() => {
    if (ideaNodes.length > prevNodesLengthRef.current) {
      prevNodesLengthRef.current = ideaNodes.length;
    }
  }, [ideaNodes]);

  // Callback for child nodes to report expansions (mark as visited if tracking is active)
  const handleNodeExpanded = useCallback((index: number) => {
    setVisitedNodes((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  return (
    <>
      {/* Render headline separately */}
      {headlineBlock && (
        <div
          css={css`
            margin-bottom: 16px;
          `}
        >
          <MarkdownWithHighlightPowers rawMarkdownContent={headlineBlock} />
        </div>
      )}

      <div css={nodesContainerStyle}>
        {ideaNodes.map((virtualNode, index) => (
          <VirtualNode
            key={index}
            virtualNode={virtualNode}
            index={index}
            totalNodes={ideaNodes.length}
            isLastNode={index === ideaNodes.length - 1}
            isHighlighted={highlightedIndex === index}
            isVisited={visitedNodes.has(index)}
            onExpand={handleNodeExpanded}
          />
        ))}
      </div>
    </>
  );
};

const nodeActionsContainer = css`
  text-align: center;
  margin: 0 auto;
  display: flex;
  justify-content: space-around;
  width: 30%;
  margin: auto;
`;

const spanIconButton = css`
  cursor: pointer;
  :hover {
    color: #1d72b9;
  }
`;

const nodeBottomRow = css`
  position: relative;
`;

const threadCountStyle = css`
  position: absolute;
  bottom: 0px;
  right: 12%;
  color: rgba(255, 255, 255, 0.7);
  // border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 4px;
  padding: 2px;
  user-select: none;
`;

const toggleContainerStyle = (isExpanded: boolean) => css`
  width: 55px;
  margin: 0 auto;
  border: 1px solid #e2e2e2;
  border-radius: 16px;
  margin-bottom: ${isExpanded ? "5px" : "0"};
  text-align: center;
  cursor: pointer;
  :hover {
    color: #1d72b9;
  }
`;

const NodeActions = (props: {
  nodeText: string;
  nodeIndex: string;
  totalNodeCount: string;
}) => {
  const startRequest = useIdeasNodeStore((state) => state.startRequest);

  const childrenNodes = usethreadModeStore((state) => state.childrenNodes);
  const appendTextToChildrenNode = usethreadModeStore(
    (state) => state.appendTextToChildrenNode
  );
  const toggleChildrenNode = usethreadModeStore(
    (state) => state.toggleChildrenNode
  );

  const [plusCircleClicked, setPlusCircleClicked] = useState(false);
  const [searchPopoverOpen, setSearchPopoverOpen] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [searchParams] = useSearchParams();
  const slugParam = searchParams.get("s");
  const hasShareableLink = slugParam !== null;
  const [
    factCheckFeatureComingSoonPopoverOpen,
    setFactCheckFeatureComingSoonPopoverOpen,
  ] = useState(false);

  const handleShareNode = () => {
    if (!hasShareableLink) {
      message.warning("This summary is not shareable yet.");
      return;
    }
    const shareUrl = buildShareUrl(
      window.location.href,
      slugParam!,
      parseInt(props.nodeIndex)
    );
    const shareTitle = `Sharpana summary node ${parseInt(props.nodeIndex) + 1}`;

    if (isMobileDevice() && isWebShareSupported()) {
      shareViaWebShareApi(shareUrl, shareTitle).catch((err) => {
        if (err.name !== "AbortError") {
          message.error("Could not share content");
        }
      });
    } else {
      setShareModalVisible(true);
    }
  };

  const handleDigDeeperOnSource = (e: any) => {
    // The callback handles expanding the toggle (because new nodes get created with expanded: true in threadModeStore::appendTextToChildrenNode)
    const req = startRequest<
      { action: string; target_text: string; parent_node_id_path: string },
      AppendTextToThreadNodeResponse
    >({
      action: "dig_deeper_on_source_2",
      target_text: props.nodeText,
      parent_node_id_path: props.nodeIndex,
    });

    req.subscribe((r) => {
      const msg = r.lastMessage as any;
      if (!msg) return;
      if (msg.response_type === "append_text_to_thread_node") {
        if (msg.text_to_append) {
          appendTextToChildrenNode(msg.node_id_path, msg.text_to_append);
        }
      }
    });

    setPlusCircleClicked(true);
  };

  return (
    <>
      <div css={nodeBottomRow}>
        {props.nodeIndex in childrenNodes ? (
          <div
            css={toggleContainerStyle(childrenNodes[props.nodeIndex].expanded)}
            onClick={() => toggleChildrenNode(props.nodeIndex)}
          >
            <span css={toggleStyle}>
              {!childrenNodes[props.nodeIndex].expanded ? "▼" : "▲"}
            </span>
          </div>
        ) : (
          <div css={nodeActionsContainer}>
            <div css={spanIconButton}>
              {plusCircleClicked &&
              (!childrenNodes[props.nodeIndex] ||
                !childrenNodes[props.nodeIndex].text) ? (
                <Spin size="small" />
              ) : (
                <div onClick={handleDigDeeperOnSource}>
                  <Popover
                    content={
                      <div
                        css={css`
                          color: white;
                          padding: 0 8px;
                        `}
                      >
                        Dig deeper on section
                      </div>
                    }
                  >
                    <PlusCircleOutlined />
                  </Popover>
                </div>
              )}
            </div>

            <div css={spanIconButton}>
              <Popover
                content={
                  <SearchActionPopover
                    setOpen={setSearchPopoverOpen}
                    parentNodeIdPath="0"
                    targetText={props.nodeText}
                  />
                }
                trigger="click"
                open={searchPopoverOpen}
                onOpenChange={(newOpen) => setSearchPopoverOpen(newOpen)}
              >
                <SearchOutlined />
              </Popover>
            </div>

            {hasShareableLink && (
              <div
                css={spanIconButton}
                onClick={handleShareNode}
                title="Share this insight"
              >
                <ShareAltOutlined />
              </div>
            )}
            {hasShareableLink && (
              <ShareModal
                isOpen={shareModalVisible}
                onClose={() => setShareModalVisible(false)}
                url={buildShareUrl(
                  window.location.href,
                  slugParam!,
                  parseInt(props.nodeIndex)
                )}
                title={`Sharpana summary node ${parseInt(props.nodeIndex) + 1}`}
              />
            )}
          </div>
        )}
        <div css={threadCountStyle}>
          🧵 ({parseInt(props.nodeIndex) + 1}/{props.totalNodeCount})
        </div>
      </div>
    </>
  );
};
